import { generateBaseStyles } from './styles/baseStyles';
import { generateHorizontalStyles } from './styles/horizontalStyles';
import { generateCompactStyles } from './styles/compactStyles';
import { generateFooterStyles } from './styles/footerStyles';
import { generateBaseLayout } from './layouts/baseLayout';
import { generateVerticalLayout } from './layouts/verticalLayout';
import { generateHorizontalLayout } from './layouts/horizontalLayout';
import { generateCompactLayout } from './layouts/compactLayout';
import { generateFooterLayout } from './layouts/footerLayout';
import { generateSquareLayout } from './layouts/squareLayout';
import { generateSquareStyles } from './styles/squareStyles';

const generatePlayerScript = (config) => `
<script>
class RadioPlayer {
    constructor(container, config) {
        this.container = container;
        this.config = config;
        this.audio = new Audio();
        this.isPlaying = false;
        this.currentVolume = 1;
        this.elapsed = 0;
        this.metadata = null;
        this.lastFetchTime = 0;
        this.fetchInProgress = false;

        this.init();
    }

    init() {
        this.audio.src = this.config.streamUrl;
        this.audio.preload = 'none';

        this.setupElements();
        this.attachEventListeners();
        this.startMetadataPolling();
        this.startTimeTracking();
    }

    setupElements() {
        this.artworkImage = this.container.querySelector('.artwork-image');
        this.artworkPlaceholder = this.container.querySelector('.artwork-placeholder');
        this.songNameDiv = this.container.querySelector('.song-name');
        this.artistNameDiv = this.container.querySelector('.artist-name');
        this.radioNameDiv = this.container.querySelector('.radio-name');
        if (this.radioNameDiv) {
            this.radioNameDiv.style.fontWeight = '600';
        }
        this.liveContainer = this.container.querySelector('.live-container');
        this.playButton = this.container.querySelector('.play-button');
        this.volumeButton = this.container.querySelector('.volume-button');
        this.volumeSlider = this.container.querySelector('.volume-slider');
        this.timeBarProgress = this.container.querySelector('.time-bar-progress');
        this.elapsedTimeSpan = this.container.querySelector('.elapsed-time');
        this.durationTimeSpan = this.container.querySelector('.duration-time');
        this.historyModal = document.querySelector('.history-modal');
        this.historyList = document.querySelector('.history-list');

        // Initialize volume state
        if (this.volumeSlider) {
            this.volumeSlider.value = 100;
            this.isMuted = false;
            this.preMuteVolume = 1;
        }

        // Apply border color if specified
        if (this.config.borderColor) {
            this.container.style.borderColor = this.config.borderColor;
        }

        if (this.volumeButton && this.volumeSlider) {
            this.setupVolumeControls();
        }

        if (this.historyModal) {
            this.attachHistoryEventListeners();
        }
    }

    setupVolumeControls() {
        const volumeContainer = this.container.querySelector('.volume-bar-container');
        let hideTimeout;

        const showVolume = () => {
            clearTimeout(hideTimeout);
            volumeContainer.classList.add('show');
        };

        const hideVolume = () => {
            hideTimeout = setTimeout(() => {
                volumeContainer.classList.remove('show');
            }, 200);
        };

        // Hover events
        this.volumeButton.addEventListener('mouseenter', showVolume);
        this.volumeButton.addEventListener('mouseleave', hideVolume);
        volumeContainer.addEventListener('mouseenter', showVolume);
        volumeContainer.addEventListener('mouseleave', hideVolume);

        // Click events
        this.volumeButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleMute();
        });

        this.volumeSlider.addEventListener('input', (e) => {
            const value = e.target.value / 100;
            this.setVolume(value);
            if (value > 0 && this.isMuted) {
                this.isMuted = false;
            }
        });

        // Close volume bar when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.volume-bar-container') && 
                !e.target.closest('.volume-button')) {
                volumeContainer.classList.remove('show');
            }
        });
    }

    attachEventListeners() {
        this.audio.addEventListener('play', () => this.updatePlayState());
        this.audio.addEventListener('pause', () => this.updatePlayState());
        this.audio.addEventListener('volumechange', () => this.updateVolumeState());

        if (this.playButton) {
            this.playButton.addEventListener('click', () => this.togglePlay());
        }
    }

    startMetadataPolling() {
        this.fetchMetadata();
        setInterval(() => this.fetchMetadata(), 10000);
    }

    startTimeTracking() {
        this.elapsedInterval = setInterval(() => {
            this.elapsed++;
            this.updateTimeDisplay();
        }, 1000);
    }

    async fetchMetadata() {
        if (this.fetchInProgress || Date.now() - this.lastFetchTime < 2000) return;
        this.fetchInProgress = true;

        try {
            const response = await fetch(this.config.apiUrl);
            const data = await response.json();
            this.metadata = data;
            this.updateMetadata(data);
            this.lastFetchTime = Date.now();
        } catch (error) {
            console.error('Error fetching metadata:', error);
        } finally {
            this.fetchInProgress = false;
        }
    }

    async togglePlay() {
        try {
            if (this.isPlaying) {
                this.audio.pause();
            } else {
                await this.audio.play();
            }
            this.isPlaying = !this.isPlaying;
            this.updatePlayState();
        } catch (error) {
            console.error('Error toggling play state:', error);
        }
    }

    updatePlayState() {
        if (this.playButton) {
            if ('${config.buttonStyle}' === 'text') {
                this.playButton.textContent = this.isPlaying ? 'Pause' : 'Play';
            } else {
                this.playButton.innerHTML = \`<i class="fas fa-\${this.isPlaying ? 'pause' : 'play'}"></i>\`;
            }
        }
    }

    setVolume(value) {
        this.audio.volume = value;
        this.currentVolume = value;
        this.isMuted = value === 0;
        this.updateVolumeState();
    }

    toggleMute() {
        if (this.audio.volume > 0) {
            this.preMuteVolume = this.audio.volume;
            this.setVolume(0);
        } else {
            this.setVolume(this.preMuteVolume);
        }
    }

    updateVolumeState() {
        if (this.volumeButton && this.volumeSlider) {
            const volume = this.audio.volume;
            this.volumeSlider.value = volume * 100;
            
            let icon = 'volume-up';
            if (volume === 0) icon = 'volume-mute';
            else if (volume < 0.33) icon = 'volume-off';
            else if (volume < 0.66) icon = 'volume-down';

            if ('${config.buttonStyle}' === 'text') {
                this.volumeButton.textContent = volume === 0 ? 'Unmute' : 'Mute';
            } else {
                this.volumeButton.innerHTML = \`<i class="fas fa-\${icon}"></i>\`;
            }
        }
    }

    formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return \`\${mins}:\${secs.toString().padStart(2, '0')}\`;
    }

    updateTimeDisplay() {
        if (this.timeBarProgress && this.metadata?.now_playing) {
            const duration = this.metadata.now_playing.duration || 0;
            const progress = duration > 0 ? (this.elapsed / duration) * 100 : 0;
            this.timeBarProgress.style.width = \`\${Math.min(progress, 100)}%\`;
        }
        
        if (this.elapsedTimeSpan) {
            this.elapsedTimeSpan.textContent = this.formatTime(this.elapsed);
        }
        if (this.durationTimeSpan && this.metadata?.now_playing) {
            this.durationTimeSpan.textContent = this.formatTime(this.metadata.now_playing.duration || 0);
        }
    }

    updateMetadata(data) {
        const nowPlaying = data.now_playing;
        if (!nowPlaying) return;

        // Reset elapsed time when new song starts
        this.elapsed = Math.max(0, data.now_playing.elapsed || 0);

        // Update artwork
        if (this.artworkImage && nowPlaying.song.art) {
            this.artworkImage.src = nowPlaying.song.art;
            this.artworkImage.style.display = 'block';
            this.artworkPlaceholder.style.display = 'none';
        } else if (this.artworkImage) {
            this.artworkImage.style.display = 'none';
            this.artworkPlaceholder.style.display = 'flex';
        }

        // Update song info
        if (this.songNameDiv) {
            this.songNameDiv.textContent = nowPlaying.song.title || '';
        }
        if (this.artistNameDiv) {
            this.artistNameDiv.textContent = nowPlaying.song.artist || '';
        }

        // Update radio name
        if (this.radioNameDiv && data.station?.name) {
            this.radioNameDiv.textContent = data.station.name;
        }

        // Update live status
        if (this.liveContainer) {
            const isLive = data.live?.is_live || false;
            this.liveContainer.style.display = isLive ? 'flex' : 'none';
            const liveText = this.liveContainer.querySelector('.live-text');
            if (liveText && isLive) {
                liveText.textContent = data.live?.streamer_name || 'LIVE';
            }
        }

        // Update history list if modal is open
        if (this.historyModal && this.historyModal.style.display === 'block') {
            this.updateHistoryList();
        }
    }

    cleanup() {
        if (this.elapsedInterval) {
            clearInterval(this.elapsedInterval);
            this.elapsedInterval = null;
        }
        if (this.metadataInterval) {
            clearInterval(this.metadataInterval);
            this.metadataInterval = null;
        }
        if (this.audio) {
            this.audio.pause();
            this.audio.src = '';
            this.isPlaying = false;
            this.currentVolume = 1;
            this.elapsed = 0;
            this.metadata = null;
            this.lastFetchTime = 0;
            this.fetchInProgress = false;
        }
        if (this.historyModal) {
            this.hideHistoryModal();
        }
    }

    attachHistoryEventListeners() {
        const historyButton = this.container.querySelector('.history-button');
        const historyModalClose = document.querySelector('.history-modal-close');
        const historyModalOverlay = document.querySelector('.history-modal-overlay');

        if (historyButton && this.historyModal) {
            historyButton.addEventListener('click', () => this.showHistoryModal());
            historyModalClose.addEventListener('click', () => this.hideHistoryModal());
            historyModalOverlay.addEventListener('click', () => this.hideHistoryModal());
        }
    }

    showHistoryModal() {
        if (this.historyModal && this.metadata?.song_history) {
            this.updateHistoryList();
            this.historyModal.style.display = 'block';
            document.body.style.overflow = 'hidden';
        }
    }

    hideHistoryModal() {
        if (this.historyModal) {
            this.historyModal.style.display = 'none';
            document.body.style.overflow = '';
        }
    }

    updateHistoryList() {
        if (!this.historyList || !this.metadata?.song_history) return;

        this.historyList.innerHTML = this.metadata.song_history.map(item => \`
            <div class="history-item">
                <div class="history-artwork">
                    <img src="\${item.song.art}" alt="\${item.song.title}" />
                </div>
                <div class="history-song-info">
                    <div class="history-song-title">\${item.song.title}</div>
                    <div class="history-artist-name">\${item.song.artist}</div>
                </div>
            </div>
        \`).join('');
    }
}

// Initialize the player
document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('radio-player');
    const config = {
        streamUrl: '${config.urls.streamUrl || ''}',
        apiUrl: '${config.urls.apiUrl || ''}',
        borderColor: '${config.colors.borderColor || 'transparent'}'
    };
    window.radioPlayer = new RadioPlayer(container, config);
});

// Cleanup on page unload
window.addEventListener('unload', () => {
    if (window.radioPlayer) {
        window.radioPlayer.cleanup();
    }
});
</script>`;

export const generateEmbedCode = (config) => {
    const { style } = config;

    // Select the appropriate layout generator based on style
    let layoutGenerator;
    switch (style) {
        case 'horizontal':
            layoutGenerator = generateHorizontalLayout;
            break;
        case 'compact':
            layoutGenerator = generateCompactLayout;
            break;
        case 'footer':
            layoutGenerator = generateFooterLayout;
            break;
        case 'square':
            layoutGenerator = generateSquareLayout;
            break;
        case 'vertical':
        default:
            layoutGenerator = generateVerticalLayout;
            break;
    }

    // Generate the layout HTML
    const layoutHtml = layoutGenerator(config);

    // Generate the base styles
    const baseStyles = generateBaseStyles({
        colors: {
            ...config.colors,
            borderColor: config.colors.borderColor || 'transparent'
        },
        dimensions: config.dimensions,
        fontFamily: config.fontFamily,
        buttonStyle: config.buttonStyle,
        fontSize: config.fontSize,
        fontSpacing: config.fontSpacing,
        textAlign: config.textAlign
    });

    // Generate additional style based on the layout type
    let additionalStyles = '';
    switch (style) {
        case 'horizontal':
            additionalStyles = generateHorizontalStyles({
                ...config,
                colors: {
                    ...config.colors,
                    borderColor: config.colors.borderColor || 'transparent'
                },
                fontSpacing: config.fontSpacing
            });
            break;
        case 'compact':
            additionalStyles = generateCompactStyles({
                ...config,
                colors: {
                    ...config.colors,
                    borderColor: config.colors.borderColor || 'transparent'
                },
                fontSpacing: config.fontSpacing
            });
            break;
        case 'footer':
            additionalStyles = generateFooterStyles({
                ...config,
                colors: {
                    ...config.colors,
                    borderColor: config.colors.borderColor || 'transparent'
                },
                fontSpacing: config.fontSpacing
            });
            break;
        case 'square':
            additionalStyles = generateSquareStyles({
                ...config,
                colors: {
                    ...config.colors,
                    borderColor: config.colors.borderColor || 'transparent'
                },
                fontSpacing: config.fontSpacing
            });
            break;
        default:
            additionalStyles = '';
            break;
    }

    // Generate the player script
    const playerScript = generatePlayerScript(config);

    // Combine all parts into the final embed code
    return `
<!-- Font Awesome -->
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css" integrity="sha512-1ycn6IcaQQ40/MKBW2W4Rhis/DbILU74C1vSrLJxCq57o941Ym01SwNsOMqvEBFlcgUa6xLiPY/NS5R+E6ztJQ==" crossorigin="anonymous" referrerpolicy="no-referrer" />

<!-- Radio Player Widget HTML -->
${layoutHtml}

<!-- Radio Player Widget CSS -->
<style>
${baseStyles}
${additionalStyles}
</style>

${playerScript}`;
}; 